import { scheduleAdaptiveContent } from "./scheduler";
import type {
  AdaptiveContentCandidate,
  AdaptivePracticePassage,
  SkillType,
} from "./types";
import { ADAPTIVE_TEMPLATE_CATALOG } from "./catalog";

function classifyCharacter(character: string): SkillType {
  if (/[A-Z]/.test(character)) return "shiftedKey";
  if (/[0-9]/.test(character)) return "number";
  if (/[^\p{L}\p{N}\s]/u.test(character)) return "punctuation";
  return "key";
}

function keySkillId(character: string): string {
  return `${classifyCharacter(character)}:${character === " " ? "space" : character}`;
}

export function skillsInText(text: string): string[] {
  const skills = new Set<string>();
  const characters = [...text];

  characters.forEach((character, index) => {
    skills.add(keySkillId(character));
    const previous = characters[index - 1];
    const previousTwo = characters[index - 2];
    if (previous !== undefined) {
      skills.add(`bigram:${previous}${character}`);
    }
    if (previousTwo !== undefined && previous !== undefined) {
      skills.add(`trigram:${previousTwo}${previous}${character}`);
    }
  });

  return [...skills];
}

function difficultyFor(text: string): number {
  const punctuationCount = [...text].filter((character) =>
    /[^\p{L}\p{N}\s]/u.test(character),
  ).length;
  if (punctuationCount >= 3 || text.length >= 88) return 3;
  if (punctuationCount >= 1 || text.length >= 68) return 2;
  return 1;
}

function curatedCandidates(): AdaptiveContentCandidate[] {
  return ADAPTIVE_TEMPLATE_CATALOG.map((template) => ({
    id: template.id,
    text: template.text,
    targetSkills: skillsInText(template.text),
    reviewSkills: skillsInText(template.text),
    difficulty: template.difficulty ?? difficultyFor(template.text),
  }));
}

function tokenFromSkillId(skillId: string): string | null {
  const separator = skillId.indexOf(":");
  if (separator < 0) return null;
  const token = skillId.slice(separator + 1);
  if (token === "space") return " ";
  if (token.length === 0 || token.length > 3) return null;
  return token;
}

function makeMicroDrill(
  skillId: string,
  index: number,
): AdaptiveContentCandidate | null {
  const token = tokenFromSkillId(skillId);
  if (token === null || token.trim().length === 0) {
    return null;
  }
  const visibleToken = token === " " ? "space" : token;
  const text =
    `Focus on ${visibleToken}: ${token} ${token} ${token}. ` +
    `Keep the motion light, accurate, and even each time ${visibleToken} appears.`;
  return {
    id: `micro-${index}-${[...skillId]
      .map((character) => character.codePointAt(0)?.toString(16) ?? "0")
      .join("-")}`,
    text,
    targetSkills: [skillId, ...skillsInText(text)],
    reviewSkills: [],
    difficulty: 1,
  };
}

function countOccurrences(text: string, token: string): number {
  if (token.length === 0) return 0;
  let count = 0;
  let offset = 0;
  while (offset <= text.length - token.length) {
    const index = text.indexOf(token, offset);
    if (index < 0) break;
    count += 1;
    offset = index + 1;
  }
  return count;
}

export function buildAdaptivePracticePassage(options: {
  targetSkillIds: readonly string[];
  reviewSkillIds?: readonly string[];
  totalPieces?: number;
  recentContentIds?: readonly string[];
  maximumDifficulty?: number;
}): AdaptivePracticePassage {
  const targetSkillIds = [...new Set(options.targetSkillIds)].slice(0, 4);
  const reviewSkillIds = [...new Set(options.reviewSkillIds ?? [])].slice(0, 3);
  const microDrills = targetSkillIds
    .map(makeMicroDrill)
    .filter((candidate): candidate is AdaptiveContentCandidate => candidate !== null);
  const scheduledItems = scheduleAdaptiveContent(
    [...microDrills, ...curatedCandidates()],
    {
      totalItems: Math.min(12, Math.max(4, options.totalPieces ?? 8)),
      targetSkills: targetSkillIds,
      reviewSkills: reviewSkillIds,
      recentContentIds: options.recentContentIds,
      maximumDifficulty: options.maximumDifficulty ?? 3,
    },
  );
  const text = scheduledItems.map((item) => item.text.trim()).join(" ");
  const coveredTargetSkillIds = targetSkillIds.filter((skillId) =>
    scheduledItems.some((item) => item.targetSkills.includes(skillId)),
  );
  const targetExposureCount = targetSkillIds.reduce((sum, skillId) => {
    const token = tokenFromSkillId(skillId);
    return token === null ? sum : sum + countOccurrences(text, token);
  }, 0);
  const identity = targetSkillIds
    .join("|")
    .split("")
    .reduce((hash, character) => (hash * 31 + character.charCodeAt(0)) >>> 0, 7)
    .toString(36);

  return {
    id: `adaptive-${identity}-${scheduledItems.length}`,
    text,
    targetSkillIds,
    reviewSkillIds,
    coveredTargetSkillIds,
    targetExposureCount,
    scheduledItems,
  };
}
